import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, Button, TextField, FormControl, InputLabel, Select, MenuItem,
  Alert, Accordion, AccordionSummary, AccordionDetails, Grid,
  IconButton, Tooltip, Collapse, List, ListItem, ListItemIcon, ListItemText,
  ListItemButton, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions,
  Paper, ToggleButton, ToggleButtonGroup
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import FolderIcon from '@mui/icons-material/Folder';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import useTheme from '../hooks/useTheme';
import mammoth from 'mammoth';
import { Document, Packer, Paragraph, TextRun } from 'docx';
import * as XLSX from 'xlsx';

export default function DocumentManager() {
  const theme = useTheme();
  const API_URL = "https://api.uxhq.com";
  
  const [files, setFiles] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [targetFolder, setTargetFolder] = useState("Brain Templates");
  const [customFilename, setCustomFilename] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [fileVersions, setFileVersions] = useState({});
  const [expandedFolders, setExpandedFolders] = useState(new Set());
  const [folderStructure, setFolderStructure] = useState({});
  const [folderTree, setFolderTree] = useState({});
  const [currentPath, setCurrentPath] = useState([]);
  const [breadcrumbPath, setBreadcrumbPath] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);
  const [editingFile, setEditingFile] = useState(null);
  const [editedContent, setEditedContent] = useState('');
  const [viewingFile, setViewingFile] = useState(null);
  const [fileContent, setFileContent] = useState('');
  const [isLoadingContent, setIsLoadingContent] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasLocalChanges, setHasLocalChanges] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);

  // Available folders from your structure
  const FOLDER_OPTIONS = [
    "Brain Templates",
    "0. VAML_JSON Structured Files",
    "1. Role Definitions",
    "2. Disciplines & Specialisations",
    "3. Skills & Taxonomies",
    "4. Frameworks, Methods & Processes",
    "5. Career Pathways",
    "6. Personal Brand",
    "7. Case Studies & People Stories",
    "8. Barriers & Challenges",
    "9. Industry Context"
  ];

  // Load files with cache busting
  const loadFiles = async (forceRefresh = false) => {
    setIsLoading(true);
    setError(null);
    try {
      const url = forceRefresh 
        ? `${API_URL}/get_docs?t=${Date.now()}`
        : `${API_URL}/get_docs`;
      
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error("Failed to fetch files");
      }
      const data = await response.json();
      const filesData = data.files || [];
      setFiles(filesData);
      
      // Build hierarchical folder structure from file paths
      const structure = {};
      const folderTree = {};
      
      filesData.forEach(filePath => {
        // Handle both string paths and object with path property
        const path = typeof filePath === 'string' ? filePath : filePath.path || filePath;
        const pathParts = path.split('/');
        
        // Build hierarchical structure
        let currentLevel = folderTree;
        pathParts.forEach((part, index) => {
          if (index === pathParts.length - 1) {
            // This is a file
            if (!currentLevel.files) currentLevel.files = [];
            const fileName = part;
            currentLevel.files.push({
              name: fileName,
              path: path,
              size: 0, // We don't have size info from the API
              last_modified: Date.now() / 1000, // We don't have timestamp info from the API
              last_modified_readable: new Date().toLocaleString(),
              originalName: fileName,
              originalPath: path,
              type: 'file'
            });
          } else {
            // This is a folder
            if (!currentLevel.folders) currentLevel.folders = {};
            if (!currentLevel.folders[part]) {
              currentLevel.folders[part] = {
                name: part,
                path: pathParts.slice(0, index + 1).join('/'),
                type: 'folder',
                level: index,
                fullPath: pathParts.slice(0, index + 1).join('/')
              };
            }
            currentLevel = currentLevel.folders[part];
          }
        });
        
        // Also maintain the old flat structure for backward compatibility
        const folder = pathParts[0] || 'Root';
        if (!structure[folder]) {
          structure[folder] = [];
        }
        const fileName = pathParts[pathParts.length - 1] || path;
        structure[folder].push({
          name: fileName,
          path: path,
          size: 0,
          last_modified: Date.now() / 1000,
          last_modified_readable: new Date().toLocaleString(),
          originalName: fileName,
          originalPath: path,
          fullPath: path,
          pathParts: pathParts
        });
      });
      
      setFolderStructure(structure);
      // Store the hierarchical structure for navigation
      setFolderTree(folderTree);
    } catch (err) {
      setError(`Failed to load files: ${err.message}`);
      console.error("Error fetching files:", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadFiles();
  }, []);

  // Dynamic theme styles - moved here to fix initialization order
  const isDarkMode = false; // You can integrate this with your theme context
  const dynamicTheme = {
    backgroundColor: isDarkMode ? '#1a1a1a' : theme.backgroundColor,
    cardBackground: isDarkMode ? '#2d2d2d' : theme.cardBackground,
    textColor: isDarkMode ? '#ffffff' : theme.textColor,
    textSecondaryColor: isDarkMode ? '#b0b0b0' : theme.textSecondaryColor,
    borderColor: isDarkMode ? '#404040' : theme.borderColor,
  };

  // Toggle folder expansion
  const toggleFolder = (folderName) => {
    const newExpanded = new Set(expandedFolders);
    if (newExpanded.has(folderName)) {
      newExpanded.delete(folderName);
    } else {
      newExpanded.add(folderName);
    }
    setExpandedFolders(newExpanded);
  };

  // Navigate to a specific folder path
  const navigateToFolder = (pathParts) => {
    setCurrentPath(pathParts);
    setBreadcrumbPath(pathParts);
  };

  // Navigate to parent folder
  const navigateToParent = () => {
    if (currentPath.length > 1) {
      const newPath = currentPath.slice(0, -1);
      setCurrentPath(newPath);
      setBreadcrumbPath(newPath);
    }
  };

  // Navigate to root
  const navigateToRoot = () => {
    setCurrentPath([]);
    setBreadcrumbPath([]);
  };

  // Get current folder contents
  const getCurrentFolderContents = () => {
    if (currentPath.length === 0) {
      // Root level - show main folders and any files that might be disguised as directories
      return Object.keys(folderStructure).map(folderName => {
        // Check if this "folder" is actually a file with an extension
        const hasFileExtension = folderName.includes('.');
        const isLikelyFile = hasFileExtension && (
          folderName.endsWith('.docx') || 
          folderName.endsWith('.txt') || 
          folderName.endsWith('.xlsx') ||
          folderName.endsWith('.xls') ||
          folderName.endsWith('.pdf') ||
          folderName.endsWith('.md')
        );
        
        if (isLikelyFile) {
          // This is actually a file, show it as editable
          return {
            name: folderName,
            type: 'file',
            path: folderName,
            fullPath: folderName,
            size: 0,
            last_modified: Date.now() / 1000,
            last_modified_readable: new Date().toLocaleString(),
            originalName: folderName,
            originalPath: folderName
          };
        } else {
          // This is a real directory
          return {
            name: folderName,
            type: 'folder',
            path: folderName,
            fullPath: folderName
          };
        }
      });
    }

    // Navigate through the tree to get current folder contents
    let currentLevel = folderTree;
    for (const part of currentPath) {
      if (currentLevel.folders && currentLevel.folders[part]) {
        currentLevel = currentLevel.folders[part];
      } else {
        return [];
      }
    }

    const contents = [];
    
    // Add subfolders
    if (currentLevel.folders) {
      Object.values(currentLevel.folders).forEach(folder => {
        contents.push({
          ...folder,
          type: 'folder'
        });
      });
    }
    
    // Add files
    if (currentLevel.files) {
      currentLevel.files.forEach(file => {
        contents.push({
          ...file,
          type: 'file'
        });
      });
    }

    return contents;
  };

  // Download file with version tracking
  const downloadFile = (filePath) => {
    setFileVersions(prev => ({
      ...prev,
      [filePath]: (prev[filePath] || 0) + 1
    }));

    const version = fileVersions[filePath] || 1;
    window.open(
      `${API_URL}/get_doc_spec/${encodeURIComponent(filePath)}?v=${version}`,
      "_blank"
    );
  };

  // Upload and update file
  const uploadFile = async () => {
    if (!selectedFile) {
      alert("Please select a file first");
      return;
    }

    setIsUploading(true);
    setError(null);
    setSuccess(null);

    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("action", "overwrite"); // Use overwrite action for updates
      
      // Use custom filename if provided, otherwise use original filename
      const filename = customFilename || selectedFile.name;
      formData.append("filename", filename);

      const response = await fetch(`${API_URL}/docs/update`, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Upload failed");
      }

      const result = await response.json();
      
      setSuccess({
        message: result.message,
        details: {
          updatedFile: {
            name: result.filename || filename,
            path: filename,
            size: result.size || 0
          },
          stats: {
            total_files_processed: 1,
            total_chunks: 0,
            failed_files_count: 0
          }
        }
      });
      
      // Force refresh files and reset form
      await loadFiles(true);
      setSelectedFile(null);
      setCustomFilename("");
      
    } catch (err) {
      setError(`Upload failed: ${err.message}`);
      console.error("Upload error:", err);
    } finally {
      setIsUploading(false);
    }
  };

  // Upload and embed file (new file)
  const uploadAndEmbed = async () => {
    if (!selectedFile) {
      alert("Please select a file first");
      return;
    }

    setIsUploading(true);
    setError(null);
    setSuccess(null);

    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      
      // Use custom filename if provided, otherwise use original filename
      const filename = customFilename || selectedFile.name;
      formData.append("filename", filename);

      const response = await fetch(`${API_URL}/upload_and_embed`, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Upload failed");
      }

      const result = await response.json();
      
      setSuccess({
        message: result.message,
        details: {
          updatedFile: {
            name: result.filename || filename,
            path: filename,
            size: result.size || 0
          },
          stats: {
            total_files_processed: 1,
            total_chunks: result.points_created || 0,
            failed_files_count: 0
          }
        }
      });
      
      // Force refresh files and reset form
      await loadFiles(true);
      setSelectedFile(null);
      setCustomFilename("");
      
    } catch (err) {
      setError(`Upload failed: ${err.message}`);
      console.error("Upload error:", err);
    } finally {
      setIsUploading(false);
    }
  };

  // Open delete dialog
  const openDeleteDialog = (file) => {
    setFileToDelete(file);
    setDeleteDialogOpen(true);
  };

  // Helper functions
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.floor(Math.log(bytes) / Math.log(k)));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp * 1000).toLocaleString();
  };

  // Read DOCX file content
  const readDocxFile = async (filePath, forceRefresh = false) => {
    console.log('readDocxFile called with path:', filePath, 'forceRefresh:', forceRefresh);
    setIsLoadingContent(true);
    setError(null);
    
    try {
      // Add cache busting parameter to ensure fresh content
      const cacheBuster = forceRefresh || Date.now();
      const url = `${API_URL}/get_doc_spec/${encodeURIComponent(filePath)}?t=${cacheBuster}`;
      console.log('Fetching from URL:', url);
      
      const response = await fetch(url, {
        // Add cache control headers to prevent caching
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      
      if (!response.ok) {
        throw new Error("Failed to fetch file");
      }
      
      const blob = await response.blob();
      const arrayBuffer = await blob.arrayBuffer();
      
      // Check if it's a supported file by looking at the file extension
      const fileExtension = filePath.split('.').pop()?.toLowerCase();
      console.log('File extension detected:', fileExtension);
      
      if (fileExtension === 'docx') {
        // Parse DOCX content using mammoth
        const result = await mammoth.extractRawText({ arrayBuffer });
        const content = result.value;
        console.log('DOCX content extracted:', content.substring(0, 100) + '...');
        console.log('Full content length:', content.length);
        
        setFileContent(content);
        setEditedContent(content);
        setViewingFile(filePath);
        setHasLocalChanges(false); // Reset local changes flag
      } else if (fileExtension === 'xlsx' || fileExtension === 'xls') {
        // For XLSX files, download directly instead of trying to parse
        console.log('XLSX file detected, downloading instead of parsing:', filePath);
        
        // Create a download link and trigger download
        const url = `${API_URL}/get_doc_spec/${encodeURIComponent(filePath)}`;
        const link = document.createElement('a');
        link.href = url;
        link.download = filePath.split('/').pop(); // Extract filename
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Show a message that the file was downloaded
        setFileContent(`XLSX file "${filePath.split('/').pop()}" has been downloaded.\n\nExcel files (.xlsx, .xls) are not supported for inline viewing. Please open the downloaded file with Microsoft Excel or a compatible application.`);
        setEditedContent('');
        setViewingFile(filePath);
        setHasLocalChanges(false);
      } else if (fileExtension === 'txt' || fileExtension === 'md') {
        // Handle text files
        const text = await blob.text();
        console.log('Text file content:', text.substring(0, 100) + '...');
        setFileContent(text);
        setEditedContent(text);
        setViewingFile(filePath);
        setHasLocalChanges(false); // Reset local changes flag
      } else {
        // For other file types, show a message
        const message = `This file type (${fileExtension}) is not supported for inline viewing. Please download the file to view its contents.`;
        console.log('Unsupported file type:', fileExtension);
        setFileContent(message);
        setEditedContent('');
        setViewingFile(filePath);
        setHasLocalChanges(false); // Reset local changes flag
      }
      
    } catch (err) {
      console.error('Error in readDocxFile:', err);
      setError(`Failed to read file: ${err.message}`);
    } finally {
      setIsLoadingContent(false);
    }
  };

  // Save edited content
  const saveEditedContent = async () => {
    if (!editingFile || !editedContent.trim()) return;
    
    console.log('Saving file with editingFile:', editingFile);
    console.log('File path:', editingFile.path);
    console.log('File name:', editingFile.name);
    
    setIsSaving(true);
    setError(null);
    
    try {
      // Determine file type and create appropriate blob
      const fileExtension = editingFile.path.split('.').pop()?.toLowerCase();
      console.log('Detected file extension:', fileExtension);
      
      let blob, mimeType, filename;
      
      if (fileExtension === 'docx') {
        console.log('Creating DOCX file...');
        // Create a proper DOCX file using the docx library
        const paragraphs = [];
        
        // Process the content line by line
        editedContent.split('\n').forEach(line => {
          const trimmedLine = line.trim();
          if (trimmedLine.length === 0) {
            // Add empty paragraph for spacing
            paragraphs.push(new Paragraph({
              children: [new TextRun({ text: ' ', size: 24 })]
            }));
            return;
          }
          
          // Check if line starts with list markers
          if (trimmedLine.match(/^[\d]+\.\s/)) {
            // Numbered list
            const text = trimmedLine.replace(/^[\d]+\.\s/, '');
            paragraphs.push(new Paragraph({
              children: [new TextRun({ text, size: 24 })],
              numbering: {
                reference: 'default-numbering',
                level: 0
              }
            }));
          } else if (trimmedLine.match(/^[-*•]\s/)) {
            // Bullet list
            const text = trimmedLine.replace(/^[-*•]\s/, '');
            paragraphs.push(new Paragraph({
              children: [new TextRun({ text, size: 24 })],
              bullet: { level: 0 }
            }));
          } else if (trimmedLine.startsWith('# ')) {
            // H1 heading
            const text = trimmedLine.replace(/^#\s/, '');
            paragraphs.push(new Paragraph({
              children: [new TextRun({ text, size: 32, bold: true })],
              spacing: { after: 200 }
            }));
          } else if (trimmedLine.startsWith('## ')) {
            // H2 heading
            const text = trimmedLine.replace(/^##\s/, '');
            paragraphs.push(new Paragraph({
              children: [new TextRun({ text, size: 28, bold: true })],
              spacing: { after: 200 }
            }));
          } else if (trimmedLine.startsWith('### ')) {
            // H3 heading
            const text = trimmedLine.replace(/^###\s/, '');
            paragraphs.push(new Paragraph({
              children: [new TextRun({ text, size: 26, bold: true })],
              spacing: { after: 200 }
            }));
          } else {
            // Regular paragraph
            paragraphs.push(new Paragraph({
              children: [new TextRun({ text: trimmedLine, size: 24 })],
              spacing: { after: 200 }
            }));
          }
        });
        
        const doc = new Document({
          sections: [{
            properties: {},
            children: paragraphs
          }]
        });
        
        console.log('DOCX document created, generating blob...');
        // Generate the DOCX file
        const docxBlob = await Packer.toBlob(doc);
        console.log('DOCX blob created:', docxBlob);
        console.log('Blob size:', docxBlob.size);
        console.log('Blob type:', docxBlob.type);
        
        blob = docxBlob;
        mimeType = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
        filename = editingFile.name; // Keep original .docx extension
        
        console.log('Final filename:', filename);
        console.log('Final mime type:', mimeType);
      } else if (fileExtension === 'txt' || fileExtension === 'md') {
        blob = new Blob([editedContent], { type: `text/${fileExtension === 'md' ? 'markdown' : 'plain'}` });
        mimeType = `text/${fileExtension === 'md' ? 'markdown' : 'plain'}`;
        filename = editingFile.name;
      } else {
        // For other file types, save as text
        blob = new Blob([editedContent], { type: 'text/plain' });
        mimeType = 'text/plain';
        filename = editingFile.name.replace(/\.[^/.]+$/, '.txt');
      }
      
      const file = new File([blob], filename, { type: mimeType });
      console.log('File object created:', file);
      console.log('File name:', file.name);
      console.log('File type:', file.type);
      console.log('File size:', file.size);
      
      const formData = new FormData();
      formData.append("file", file);
      formData.append("action", "overwrite");
      // For DOCX files, ensure we're sending the correct filename that preserves the extension
      const filenameToSend = fileExtension === 'docx' ? filename : editingFile.path;
      formData.append("filename", filenameToSend);
      
      console.log('Filename being sent to backend:', filenameToSend);
      console.log('FormData created, sending to backend...');
      const response = await fetch(`${API_URL}/docs/update`, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Save failed");
      }

      const result = await response.json();
      console.log('Backend response:', result);
      
      setSuccess({
        message: "File updated successfully",
        details: {
          updatedFile: {
            name: filename,
            path: editingFile.path,
            size: result.size || 0
          },
          stats: {
            total_files_processed: 1,
            total_chunks: 0,
            failed_files_count: 0
          }
        }
      });
      
      // Update local content
      setFileContent(editedContent);
      setEditedContent(editedContent); // Keep edited content as current content
      setHasLocalChanges(false); // Reset local changes flag
      setEditModalOpen(false); // Close edit modal after successful save
      
      // Refresh file list
      await loadFiles(true);
      
      // Force refresh the content from server to verify the update
      console.log('Force refreshing content from server after save...');
      const verifyUpdate = async (retryCount = 0) => {
        try {
          console.log(`Attempting to verify update, retry count: ${retryCount}`);
          await readDocxFile(editingFile.path, true);
          console.log('Update verification successful');
        } catch (err) {
          console.error('Update verification failed:', err);
          if (retryCount < 3) {
            console.log(`Retrying verification in ${(retryCount + 1) * 1000}ms...`);
            setTimeout(() => verifyUpdate(retryCount + 1), (retryCount + 1) * 1000);
          } else {
            console.error('Max retries reached for update verification');
            setError('Failed to verify file update. You can manually refresh to see the latest content.');
          }
        }
      };
      
      setTimeout(() => verifyUpdate(), 1000); // Initial delay to ensure server has processed the update
      
      // Show success message
      console.log('File saved successfully, local state updated');
      console.log('Current fileContent:', editedContent.substring(0, 100) + '...');
      
    } catch (err) {
      setError(`Save failed: ${err.message}`);
      console.error("Save error:", err);
    } finally {
      setIsSaving(false);
    }
  };

  // Cancel editing
  const cancelEditing = () => {
    setEditedContent(fileContent); // Reset to original content
    setEditModalOpen(false); // Close edit modal
    setHasLocalChanges(false); // Reset local changes flag
  };

  // Close file viewer
  const closeFileViewer = () => {
    setViewingFile(null);
    setFileContent('');
    setEditedContent('');
    setEditingFile(null); // Clear editing file reference
    setHasLocalChanges(false); // Reset local changes flag
    setEditModalOpen(false); // Close edit modal
  };

  // Refresh local content for a specific file
  const refreshLocalContent = async (filePath) => {
    console.log('Refreshing local content for:', filePath);
    setIsLoadingContent(true);
    try {
      // Force cache busting for refresh
      const cacheBuster = Date.now();
      const url = `${API_URL}/get_doc_spec/${encodeURIComponent(filePath)}?t=${cacheBuster}`;
      console.log('Refreshing from URL:', url);
      
      const response = await fetch(url, {
        // Add cache control headers to prevent caching
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      
      if (!response.ok) {
        throw new Error("Failed to fetch updated file");
      }
      
      const blob = await response.blob();
      const arrayBuffer = await blob.arrayBuffer();
      
      // Check if it's a supported file by looking at the file extension
      const fileExtension = filePath.split('.').pop()?.toLowerCase();
      console.log('Refreshing file extension detected:', fileExtension);
      
      if (fileExtension === 'docx') {
        // Parse DOCX content using mammoth
        const result = await mammoth.extractRawText({ arrayBuffer });
        const content = result.value;
        console.log('Refreshed DOCX content:', content.substring(0, 100) + '...');
        console.log('Refreshed content length:', content.length);
        
        setFileContent(content);
        setEditedContent(content);
        setHasLocalChanges(false); // Reset local changes flag
      } else if (fileExtension === 'xlsx' || fileExtension === 'xls') {
        // For XLSX files, show download message instead of trying to parse
        console.log('Refreshing XLSX file - showing download message:', filePath);
        setFileContent(`XLSX file "${filePath.split('/').pop()}" is available for download.\n\nExcel files (.xlsx, .xls) are not supported for inline viewing. Please use the download button to get the file.`);
        setEditedContent('');
        setHasLocalChanges(false);
      } else if (fileExtension === 'txt' || fileExtension === 'md') {
        // Handle text files
        const text = await blob.text();
        console.log('Refreshed text content:', text.substring(0, 100) + '...');
        setFileContent(text);
        setEditedContent(text);
        setHasLocalChanges(false); // Reset local changes flag
      }
      
      console.log('Local content refreshed successfully');
    } catch (err) {
      console.error('Error refreshing local content:', err);
      setError(`Failed to refresh content: ${err.message}`);
    } finally {
      setIsLoadingContent(false);
    }
  };

  const cardStyle = {
    borderRadius: 4,
    boxShadow: isDarkMode 
      ? '0 8px 32px rgba(0, 0, 0, 0.4), 0 2px 8px rgba(0, 0, 0, 0.2)' 
      : '0 8px 32px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04)',
    border: `1px solid ${dynamicTheme.borderColor}`,
    background: dynamicTheme.cardBackground,
    backdropFilter: 'blur(10px)',
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: '3px',
      background: `linear-gradient(90deg, ${theme.primaryColor}, ${theme.secondaryColor})`,
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Upload Section */}
      <Card sx={{ ...cardStyle, p: 0 }}>
        <Accordion 
          expanded={true}
          sx={{ 
            boxShadow: 'none', 
            '&:before': { display: 'none' }, 
            background: 'transparent',
            '& .MuiAccordionSummary-root': {
              px: 3,
              py: 2,
              background: `linear-gradient(135deg, ${theme.primaryColor}08, ${theme.secondaryColor}08)`,
              cursor: 'default'
            },
            '& .MuiAccordionSummary-expandIconWrapper': {
              display: 'none'
            }
          }}
        >
          <AccordionSummary>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                background: `linear-gradient(135deg, ${theme.primaryColor}, ${theme.linkHoverColor})`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: `0 4px 12px ${theme.primaryColor}30`
              }}>
                <CloudUploadIcon sx={{ color: '#fff', fontSize: 20 }} />
              </Box>
              <Typography variant="h6" fontWeight={700} sx={{ color: dynamicTheme.textColor }}>
                Document Update
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Box sx={{ width: '100%' }}>
                  <input
                    type="file"
                    onChange={(e) => setSelectedFile(e.target.files[0])}
                    style={{ display: 'none' }}
                    id="file-upload"
                  />
                  <label htmlFor="file-upload">
                    <Button
                      variant="outlined"
                      component="span"
                      fullWidth
                      sx={{
                        borderColor: dynamicTheme.borderColor,
                        color: dynamicTheme.textColor,
                        borderRadius: 2,
                        py: 2,
                        borderWidth: 2,
                        '&:hover': { 
                          borderColor: theme.primaryColor, 
                          color: theme.primaryColor,
                          background: `${theme.primaryColor}08`
                        }
                      }}
                    >
                      Choose File
                    </Button>
                  </label>
                  <Typography variant="caption" sx={{ 
                    color: dynamicTheme.textSecondaryColor, 
                    mt: 1, 
                    display: 'block',
                    textAlign: 'center'
                  }}>
                    Select a document to update
                  </Typography>
                </Box>
                {selectedFile && (
                  <Box sx={{ mt: 1, p: 2, bgcolor: `${theme.primaryColor}08`, borderRadius: 2 }}>
                    <Typography variant="body2" sx={{ color: dynamicTheme.textColor, fontWeight: 500 }}>
                      Selected: {selectedFile.name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: dynamicTheme.textSecondaryColor }}>
                      Size: {formatFileSize(selectedFile.size)}
                    </Typography>
                  </Box>
                )}
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel sx={{ 
                    color: dynamicTheme.textSecondaryColor, 
                    fontWeight: 500 
                  }}>
                    Target Folder
                  </InputLabel>
                  <Select
                    value={targetFolder}
                    label="Target Folder"
                    onChange={(e) => setTargetFolder(e.target.value)}
                    sx={{
                      color: dynamicTheme.textColor,
                      '& .MuiOutlinedInput-notchedOutline': { 
                        borderColor: dynamicTheme.borderColor,
                        borderWidth: 2
                      },
                      '& .MuiSvgIcon-root': { color: dynamicTheme.textSecondaryColor },
                      '& .MuiSelect-select': { fontSize: 16 }
                    }}
                  >
                    {FOLDER_OPTIONS.map(folder => (
                      <MenuItem key={folder} value={folder} sx={{
                        color: dynamicTheme.textColor,
                        '&:hover': { background: theme.hoverColor },
                        '&.Mui-selected': { 
                          background: `${theme.primaryColor}20`,
                          '&:hover': { background: `${theme.primaryColor}30` }
                        }
                      }}>
                        {folder}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                <TextField
                  fullWidth
                  label="Custom Filename (optional)"
                  value={customFilename}
                  onChange={(e) => setCustomFilename(e.target.value)}
                  placeholder="Leave blank to keep original name"
                  sx={{
                    '& .MuiInputLabel-root': { 
                      color: dynamicTheme.textSecondaryColor,
                      fontWeight: 500
                    },
                    '& .MuiInputBase-input': { 
                      color: dynamicTheme.textColor,
                      fontSize: 16
                    },
                    '& .MuiOutlinedInput-notchedOutline': { 
                      borderColor: dynamicTheme.borderColor,
                      borderWidth: 2
                    },
                    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.primaryColor
                    },
                    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.primaryColor,
                      borderWidth: 2
                    }
                  }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={uploadFile}
                    disabled={isUploading || !selectedFile}
                    startIcon={isUploading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
                    sx={{
                      borderColor: dynamicTheme.borderColor,
                      color: '#white',
                      borderRadius: 2,
                      py: 1.5,
                      px: 3,
                      fontWeight: 600,
                      flex: 1,
                      '&:hover': { 
                        borderColor: theme.primaryColor, 
                        color: '#white',
                        background: `${theme.primaryColor}08`
                      }
                    }}
                  >
                    {isUploading ? 'Processing...' : 'Update'}
                  </Button>
                </Box>
              </Grid>
            </Grid>

            {error && (
              <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
                {error}
              </Alert>
            )}

            {success && (
              <Alert severity="success" sx={{ mt: 2, borderRadius: 2 }}>
                <Typography variant="body1" fontWeight={600}>
                  {success.message}
                </Typography>
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2">
                    <strong>File:</strong> {success.details.updatedFile.name}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Path:</strong> {success.details.updatedFile.path}
                  </Typography>
                  {success.details.updatedFile.size > 0 && (
                    <Typography variant="body2">
                      <strong>Size:</strong> {formatFileSize(success.details.updatedFile.size)}
                    </Typography>
                  )}
                  <Typography variant="body2">
                    <strong>Status:</strong> {success.details.stats.total_chunks > 0 ? 'Embedded in Qdrant' : 'Updated locally'}
                  </Typography>
                  {success.details.stats.total_chunks > 0 && (
                    <Typography variant="body2">
                      <strong>Chunks Created:</strong> {success.details.stats.total_chunks}
                    </Typography>
                  )}
                </Box>
              </Alert>
            )}
          </AccordionDetails>
        </Accordion>
      </Card>

      {/* Document Browser Section */}
      <Card sx={{ ...cardStyle, p: 0 }}>
        <Accordion 
          expanded={true}
          sx={{ 
            boxShadow: 'none', 
            '&:before': { display: 'none' }, 
            background: 'transparent',
            '& .MuiAccordionSummary-root': {
              px: 3,
              py: 2,
              background: `linear-gradient(135deg, ${theme.secondaryColor}08, ${theme.primaryColor}08)`,
              cursor: 'default'
            },
            '& .MuiAccordionSummary-expandIconWrapper': {
              display: 'none'
            }
          }}
        >
          <AccordionSummary>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
              <Box sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                background: `linear-gradient(135deg, ${theme.secondaryColor}, ${theme.primaryColor})`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: `0 4px 12px ${theme.secondaryColor}30`
              }}>
                <InsertDriveFileIcon sx={{ color: '#fff', fontSize: 20 }} />
              </Box>
              <Typography variant="h6" fontWeight={700} sx={{ color: dynamicTheme.textColor }}>
                Document Browser ({files.length})
              </Typography>
            </Box>
            <Button
              variant="outlined"
              onClick={() => loadFiles(true)}
              startIcon={<RefreshIcon />}
              sx={{
                borderColor: dynamicTheme.textSecondaryColor,
                color: dynamicTheme.textSecondaryColor,
                borderRadius: 2,
                px: 2,
                py: 1,
                fontWeight: 600,
                '&:hover': { 
                  borderColor: theme.primaryColor, 
                  color: theme.primaryColor,
                  background: `${theme.primaryColor}08`
                }
              }}
            >
              Refresh
            </Button>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 3 }}>
            {isLoading && files.length === 0 ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : error ? (
              <Alert severity="error" sx={{ borderRadius: 2 }}>
                {error}
              </Alert>
            ) : files.length === 0 ? (
              <Box sx={{ textAlign: 'center', p: 4, color: dynamicTheme.textSecondaryColor }}>
                No documents found
              </Box>
            ) : (
              <Box>
                {/* Breadcrumb Navigation */}
                <Box sx={{ mb: 3, p: 2, background: `${theme.primaryColor}08`, borderRadius: 2, border: `1px solid ${theme.primaryColor}20` }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Button
                      variant="text"
                      onClick={navigateToRoot}
                      sx={{ 
                        color: theme.primaryColor, 
                        fontWeight: 600,
                        '&:hover': { background: `${theme.primaryColor}12` }
                      }}
                    >
                      🏠 Root
                    </Button>
                    
                    {breadcrumbPath.map((part, index) => (
                      <Box key={index} sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography sx={{ color: dynamicTheme.textSecondaryColor, mx: 1 }}>/</Typography>
                        <Button
                          variant="text"
                          onClick={() => navigateToFolder(breadcrumbPath.slice(0, index + 1))}
                          sx={{ 
                            color: theme.primaryColor, 
                            fontWeight: 600,
                            '&:hover': { background: `${theme.primaryColor}12` }
                          }}
                        >
                          {part}
                        </Button>
                      </Box>
                    ))}
                  </Box>
                  
                  {/* Current Path Display */}
                  <Typography variant="body2" sx={{ color: dynamicTheme.textSecondaryColor, mt: 1 }}>
                    📍 Current Location: {breadcrumbPath.length > 0 ? breadcrumbPath.join(' / ') : 'Root'}
                  </Typography>
                </Box>

                {/* Folder and File List */}
                {getCurrentFolderContents().map((item, index) => (
                  <Box key={item.path || item.fullPath} sx={{ mb: 2 }}>
                    {item.type === 'folder' ? (
                      // Folder Item
                      <ListItemButton
                        onClick={() => navigateToFolder([...currentPath, item.name])}
                        sx={{
                          borderRadius: 2,
                          mb: 1,
                          background: `${theme.primaryColor}08`,
                          border: `1px solid ${theme.primaryColor}20`,
                          '&:hover': { 
                            background: `${theme.primaryColor}12`,
                            borderColor: theme.primaryColor,
                            transform: 'translateX(4px)',
                            transition: 'all 0.2s ease-in-out'
                          },
                          transition: 'all 0.2s ease-in-out'
                        }}
                      >
                        <ListItemIcon>
                          <FolderIcon sx={{ color: theme.primaryColor }} />
                        </ListItemIcon>
                        <ListItemText 
                          primary={item.name}
                          secondary={`📁 ${item.fullPath}`}
                          primaryTypographyProps={{ fontWeight: 600, color: dynamicTheme.textColor }}
                          secondaryTypographyProps={{ color: dynamicTheme.textSecondaryColor }}
                        />
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="caption" sx={{ color: dynamicTheme.textSecondaryColor }}>
                            Click to open
                          </Typography>
                          <ExpandMoreIcon sx={{ color: dynamicTheme.textSecondaryColor }} />
                        </Box>
                      </ListItemButton>
                    ) : (
                      // File Item
                      <ListItem
                        sx={{
                          borderRadius: 2,
                          mb: 1,
                          background: index % 2 === 0 ? `${theme.primaryColor}04` : 'transparent',
                          border: `1px solid ${theme.primaryColor}10`,
                          '&:hover': { 
                            background: `${theme.primaryColor}08`,
                            borderColor: theme.primaryColor,
                            transform: 'translateX(2px)',
                            transition: 'all 0.2s ease-in-out'
                          },
                          transition: 'all 0.2s ease-in-out'
                        }}
                      >
                        <ListItemIcon>
                          <InsertDriveFileIcon sx={{ color: theme.secondaryColor }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={item.name}
                          secondary={
                            <Box>
                              <Typography variant="caption" sx={{ color: dynamicTheme.textSecondaryColor }}>
                                📁 {item.path}
                              </Typography>
                              <br />
                              <Typography variant="caption" sx={{ color: dynamicTheme.textSecondaryColor }}>
                                💾 {formatFileSize(item.size)} • 📅 {item.last_modified_readable || formatDate(item.last_modified)}
                              </Typography>
                            </Box>
                          }
                          primaryTypographyProps={{ fontWeight: 500, color: dynamicTheme.textColor }}
                        />
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="View File">
                            <IconButton
                              size="small"
                              onClick={() => readDocxFile(item.path, true)}
                              sx={{ color: theme.primaryColor }}
                            >
                              <VisibilityIcon />
                            </IconButton>
                          </Tooltip>
                          {/* Only show Edit button for non-XLSX files */}
                          {!item.path.toLowerCase().endsWith('.xlsx') && !item.path.toLowerCase().endsWith('.xls') && (
                            <Tooltip title="Edit File">
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => {
                                  console.log('Edit button clicked for file:', item);
                                  const fileToEdit = {
                                    ...item,
                                    name: item.name || item.path.split('/').pop(),
                                    path: item.path,
                                    originalName: item.name || item.path.split('/').pop(),
                                    originalPath: item.path
                                  };
                                  setEditingFile(fileToEdit);
                                  
                                  if (viewingFile === item.path && fileContent && fileContent.trim()) {
                                    setEditedContent(fileContent);
                                    setViewingFile(item.path);
                                  } else {
                                    readDocxFile(item.path, true);
                                  }
                                  
                                  setEditModalOpen(true);
                                }}
                                startIcon={<EditIcon />}
                                sx={{ 
                                  color: theme.secondaryColor,
                                  borderColor: theme.secondaryColor,
                                  '&:hover': {
                                    borderColor: theme.primaryColor,
                                    color: theme.primaryColor,
                                    backgroundColor: `${theme.primaryColor}08`
                                  }
                                }}
                              >
                                Edit
                              </Button>
                            </Tooltip>
                          )}
                          <Tooltip title="Delete File">
                            <IconButton
                              size="small"
                              onClick={() => openDeleteDialog(item)}
                              sx={{ color: '#f44336' }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </ListItem>
                    )}
                  </Box>
                ))}
                
                {/* Back to Parent Button */}
                {currentPath.length > 0 && (
                  <Box sx={{ mt: 3, textAlign: 'center' }}>
                    <Button
                      variant="outlined"
                      onClick={navigateToParent}
                      startIcon={<ExpandMoreIcon sx={{ transform: 'rotate(90deg)' }} />}
                      sx={{
                        borderColor: theme.primaryColor,
                        color: theme.primaryColor,
                        '&:hover': {
                          background: `${theme.primaryColor}08`,
                          borderColor: theme.linkHoverColor
                        }
                      }}
                    >
                      Back to Parent Folder
                    </Button>
                  </Box>
                )}
              </Box>
            )}
          </AccordionDetails>
        </Accordion>
      </Card>

      {/* Inline File Viewer (View Only) */}
      {viewingFile && !editModalOpen && (
        <Card sx={{ ...cardStyle, p: 0 }}>
          <Accordion 
            expanded={true}
            sx={{ 
              boxShadow: 'none', 
              '&:before': { display: 'none' }, 
              background: 'transparent',
              '& .MuiAccordionSummary-root': {
                px: 3,
                py: 2,
                background: `linear-gradient(135deg, ${theme.primaryColor}08, ${theme.secondaryColor}08)`,
                cursor: 'default'
              },
              '& .MuiAccordionSummary-expandIconWrapper': {
                display: 'none'
              }
            }}
          >
            <AccordionSummary>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                <Box sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${theme.primaryColor}, ${theme.secondaryColor})`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: `0 4px 12px ${theme.primaryColor}30`
                }}>
                  <InsertDriveFileIcon sx={{ color: '#fff', fontSize: 20 }} />
                </Box>
                <Typography variant="h6" fontWeight={700} sx={{ color: dynamicTheme.textColor }}>
                  {viewingFile.split('/').pop()}
                </Typography>
                <Typography variant="body2" sx={{ color: dynamicTheme.textSecondaryColor }}>
                  {viewingFile}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  onClick={(e) => {
                    e.stopPropagation();
                    refreshLocalContent(viewingFile);
                  }}
                  startIcon={<RefreshIcon />}
                  sx={{
                    borderColor: dynamicTheme.textSecondaryColor,
                    color: dynamicTheme.textSecondaryColor,
                    borderRadius: 2,
                    px: 2,
                    py: 1,
                    fontWeight: 600,
                    '&:hover': { 
                      borderColor: theme.primaryColor, 
                      color: theme.primaryColor,
                      background: `${theme.primaryColor}08`
                    }
                  }}
                >
                  Refresh
                </Button>
                <Button
                  variant="outlined"
                  onClick={(e) => {
                    e.stopPropagation();
                    downloadFile(viewingFile);
                  }}
                  startIcon={<DownloadIcon />}
                  sx={{
                    borderColor: dynamicTheme.textSecondaryColor,
                    color: dynamicTheme.textSecondaryColor,
                    borderRadius: 2,
                    px: 2,
                    py: 1,
                    fontWeight: 600,
                    '&:hover': { 
                      borderColor: theme.secondaryColor, 
                      color: theme.secondaryColor,
                      background: `${theme.secondaryColor}08`
                    }
                  }}
                >
                  Download
                </Button>
                <Button
                  variant="outlined"
                  onClick={(e) => {
                    e.stopPropagation();
                    closeFileViewer();
                  }}
                  sx={{
                    borderColor: dynamicTheme.textSecondaryColor,
                    color: dynamicTheme.textSecondaryColor,
                    borderRadius: 2,
                    px: 2,
                    py: 1,
                    fontWeight: 600,
                    '&:hover': { 
                      borderColor: theme.primaryColor, 
                      color: theme.primaryColor,
                      background: `${theme.primaryColor}08`
                    }
                  }}
                >
                  Close
                </Button>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 3 }}>
              {isLoadingContent ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <Paper 
                  sx={{ 
                    p: 3, 
                    background: dynamicTheme.backgroundColor,
                    border: `1px solid ${dynamicTheme.borderColor}`,
                    borderRadius: 2,
                    minHeight: '400px',
                    maxHeight: '600px',
                    overflow: 'auto'
                  }}
                >
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      color: dynamicTheme.textColor,
                      whiteSpace: 'pre-wrap',
                      lineHeight: 1.6,
                      fontFamily: 'inherit'
                    }}
                  >
                    {fileContent || 'No content to display'}
                  </Typography>
                </Paper>
              )}
            </AccordionDetails>
          </Accordion>
        </Card>
      )}

      {/* Edit Modal */}
      <Dialog
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            background: dynamicTheme.cardBackground,
            border: `1px solid ${dynamicTheme.borderColor}`,
            borderRadius: 2,
            minHeight: '80vh',
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{ 
          color: dynamicTheme.textColor,
          background: `linear-gradient(135deg, ${theme.primaryColor}08, ${theme.secondaryColor}08)`,
          borderBottom: `1px solid ${dynamicTheme.borderColor}`,
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}>
          <Box sx={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            background: `linear-gradient(135deg, ${theme.primaryColor}, ${theme.secondaryColor})`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: `0 4px 12px ${theme.primaryColor}30`
          }}>
            <EditIcon sx={{ color: '#fff', fontSize: 16 }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" fontWeight={700}>
              Edit Document
            </Typography>
            <Typography variant="body2" sx={{ color: dynamicTheme.textSecondaryColor }}>
              {editingFile?.path || 'Loading...'}
            </Typography>
          </Box>
          <IconButton
            onClick={() => setEditModalOpen(false)}
            sx={{ 
              color: dynamicTheme.textSecondaryColor,
              '&:hover': { 
                color: theme.primaryColor,
                background: `${theme.primaryColor}08`
              }
            }}
          >
            <CancelIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {isLoadingContent ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ p: 3 }}>
              {/* File type conversion notice */}
              {editingFile?.path?.endsWith('.docx') && (
                <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                  <Typography variant="body2">
                    <strong>Note:</strong> When editing DOCX files, the content will be converted to text for editing, 
                    but will be saved back as a proper DOCX file preserving the original format.
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    <strong>Formatting Tips:</strong> Use # for H1, ## for H2, ### for H3, - or * for bullet lists, 1. for numbered lists.
                  </Typography>
                </Alert>
              )}
              
              {/* Local changes indicator */}
              {hasLocalChanges && (
                <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
                  <Typography variant="body2">
                    <strong>⚠️ Local Changes:</strong> You have unsaved changes. Click "Save Changes" to update the file.
                  </Typography>
                </Alert>
              )}
              
              {/* Editor Toolbar */}
              <Paper sx={{ mb: 2, p: 2, border: `1px solid ${dynamicTheme.borderColor}`, borderRadius: 2 }}>
                <ToggleButtonGroup
                  value={[]}
                  onChange={(event, newAlignment) => {
                    // Basic formatting - you can enhance this later
                  }}
                  aria-label="text formatting"
                  sx={{
                    '& .MuiToggleButton-root': {
                      border: `1px solid ${dynamicTheme.borderColor}`,
                      borderRadius: 1,
                      '&.Mui-selected': {
                        backgroundColor: theme.primaryColor,
                        color: '#fff',
                        borderColor: theme.primaryColor
                      },
                      '&:hover': {
                        backgroundColor: `${theme.primaryColor}08`,
                        borderColor: theme.primaryColor
                      }
                    }
                  }}
                >
                  <ToggleButton value="bold" aria-label="bold">
                    <FormatBoldIcon />
                  </ToggleButton>
                  <ToggleButton value="italic" aria-label="italic">
                    <FormatItalicIcon />
                  </ToggleButton>
                  <ToggleButton value="underline" aria-label="underline">
                    <FormatUnderlinedIcon />
                  </ToggleButton>
                  <ToggleButton value="bullet" aria-label="bulleted-list">
                    <FormatListBulletedIcon />
                  </ToggleButton>
                  <ToggleButton value="numbered" aria-label="numbered-list">
                    <FormatListNumberedIcon />
                  </ToggleButton>
                  <ToggleButton value="left" aria-label="align-left">
                    <FormatAlignLeftIcon />
                  </ToggleButton>
                  <ToggleButton value="center" aria-label="align-center">
                    <FormatAlignCenterIcon />
                  </ToggleButton>
                  <ToggleButton value="right" aria-label="align-right">
                    <FormatAlignRightIcon />
                  </ToggleButton>
                </ToggleButtonGroup>
              </Paper>

              {/* Editor */}
              <TextField
                fullWidth
                multiline
                rows={20}
                variant="outlined"
                value={editedContent}
                onChange={(e) => {
                  setEditedContent(e.target.value);
                  // Track if content has been modified from original
                  setHasLocalChanges(e.target.value !== fileContent);
                }}
                sx={{
                  '& .MuiInputBase-root': {
                    background: dynamicTheme.backgroundColor,
                    color: dynamicTheme.textColor,
                    fontFamily: 'inherit',
                    fontSize: '14px',
                    lineHeight: 1.6,
                    minHeight: '500px'
                  },
                  '& .MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.primaryColor,
                    borderWidth: 2
                  },
                  '& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.primaryColor
                  }
                }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ 
          p: 3, 
          borderTop: `1px solid ${dynamicTheme.borderColor}`,
          background: `linear-gradient(135deg, ${theme.primaryColor}02, ${theme.secondaryColor}02)`
        }}>
          <Button
            variant="outlined"
            onClick={cancelEditing}
            startIcon={<CancelIcon />}
            sx={{
              borderColor: dynamicTheme.borderColor,
              color: dynamicTheme.textSecondaryColor,
              borderRadius: 2,
              px: 3,
              py: 1.5,
              fontWeight: 600,
              '&:hover': { 
                borderColor: theme.primaryColor, 
                color: theme.primaryColor,
                background: `${theme.primaryColor}08`
              }
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={saveEditedContent}
            disabled={isSaving}
            startIcon={isSaving ? <CircularProgress size={20} /> : <SaveIcon />}
            sx={{
              background: `linear-gradient(135deg, ${theme.primaryColor}, ${theme.secondaryColor})`,
              borderRadius: 2,
              px: 3,
              py: 1.5,
              fontWeight: 600,
              boxShadow: `0 4px 16px ${theme.primaryColor}40`,
              '&:hover': { 
                background: `linear-gradient(135deg, ${theme.linkHoverColor}, ${theme.primaryColor})`,
                boxShadow: `0 6px 20px ${theme.primaryColor}60`
              }
            }}
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{
          sx: {
            background: dynamicTheme.cardBackground,
            border: `1px solid ${dynamicTheme.borderColor}`,
            borderRadius: 2
          }
        }}
      >
        <DialogTitle sx={{ color: dynamicTheme.textColor }}>
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: dynamicTheme.textColor }}>
            Are you sure you want to delete "{fileToDelete?.name}"?
          </Typography>
          <Typography variant="body2" sx={{ color: dynamicTheme.textSecondaryColor, mt: 1 }}>
            This action cannot be undone. The file will be removed from both local storage and the Qdrant database.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            sx={{ color: dynamicTheme.textSecondaryColor }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
